import React, { useEffect, useState } from 'react';
import { TrendingDown, TrendingUp, Music2, AlertOctagon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toTitleCase } from '../utils/displayNormalization';
import ContentMixPie from './ContentMixPie';
import ShowAnalysis from './ShowAnalysis';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ExecutiveSummary — bottom-of-page recap card for the Clock View.
 *
 * Three side-by-side columns:
 *   • Worst Breaks (negative-delta gaps, red accent)
 *   • Top Audience-Building Songs (highest positive delta during play)
 *   • Best Breaks (highest delta gaps, mutually exclusive with worst)
 *
 * The shape of `summary` is exactly what the backend
 * /api/broadcast/timeline?include_metrics=true endpoint returns:
 *   { worst_breaks: [...], hero_songs: [...], best_breaks: [...] }
 */

const SEVERITY_HEX = {
  severe_loss: '#dc2626',
  significant_loss: '#ea580c',
  minor_loss: '#facc15',
  stable: '#6b7280',
  minor_gain: '#22c55e',
  strong_gain: '#16a34a',
  unknown: '#374151',
};

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('el-GR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '—';
  }
};

const formatDuration = (s) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return sec ? `${m}m ${sec}s` : `${m}m`;
};

const DeltaPill = ({ value, pct }) => {
  if (value == null) return null;
  const colour =
    value < 0 ? '#fca5a5' : value > 0 ? '#86efac' : '#cbd5e1';
  const arrow = value < 0 ? '▼' : value > 0 ? '▲' : '■';
  return (
    <span
      className="font-bold text-base tabular-nums"
      style={{ color: colour }}
    >
      {value > 0 ? '+' : ''}
      {value} {arrow}
      {pct != null && (
        <span className="ml-1 text-[10px] font-normal opacity-75">
          ({pct > 0 ? '+' : ''}
          {pct}%)
        </span>
      )}
    </span>
  );
};

const BreakRow = ({ item, badgeHex, variant = 'best' }) => {
  const { t } = useLanguage();
  const time = formatTime(item.start);
  const dur = formatDuration(item.duration_seconds);
  const before = item.listeners_before;
  const after = item.listeners_after;
  // v2.13.20 (2026-02): Operator request — colour the WORST breaks
  // red just like villain songs. Best breaks stay neutral so green
  // gains pop in the DeltaPill.
  const isWorst = variant === 'worst';
  const containerCls = isWorst
    ? 'bg-red-950/40 border border-red-900/40 rounded-md px-3 py-2.5 space-y-1.5'
    : 'bg-white/5 border border-white/10 rounded-md px-3 py-2.5 space-y-1.5';
  return (
    <div
      className={containerCls}
      data-testid={isWorst ? 'exec-summary-worst-break-row' : 'exec-summary-break-row'}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-white/55 tabular-nums">
          {time} · {dur}
        </div>
        <span
          className="text-[9px] uppercase tracking-wide font-semibold"
          style={{ color: badgeHex }}
        >
          {item.severity_band
            ? (t(`severityBand_${item.severity_band}`) || item.severity_band.replace(/_/g, ' '))
            : t('severityBand_unknown')}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <DeltaPill value={item.delta} pct={item.delta_pct} />
        <div className="text-[10px] text-white/40 tabular-nums">
          {before} → {after}
        </div>
      </div>
    </div>
  );
};

const SongRow = ({ item, variant = 'hero' }) => {
  const time = formatTime(item.start);
  const isVillain = variant === 'villain';
  // Villain rows get a red-tinted background + red MUSIC badge so the
  // operator can instantly tell which 3 songs caused listener drops.
  const containerCls = isVillain
    ? 'bg-red-950/40 border border-red-900/40 rounded-md px-3 py-2.5 space-y-1.5'
    : 'bg-white/5 border border-white/10 rounded-md px-3 py-2.5 space-y-1.5';
  const badgeCls = isVillain ? 'text-red-400' : 'text-green-400';
  return (
    <div
      className={containerCls}
      data-testid={isVillain ? 'exec-summary-villain-song-row' : 'exec-summary-song-row'}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-white/55 tabular-nums">{time}</div>
        <span className={`text-[9px] uppercase tracking-wide font-semibold ${badgeCls}`}>
          MUSIC
        </span>
      </div>
      <div className="text-[12px] text-white/85 truncate">
        <span className="font-medium">{toTitleCase(item.artist || '')}</span>
        <span className="text-white/35 mx-1.5">—</span>
        <span>{toTitleCase(item.title || '')}</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <DeltaPill value={item.delta} />
        <div className="text-[10px] text-white/40 tabular-nums">
          {item.start_listeners} → {item.end_listeners}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, subtitle, accent, icon, items, renderRow, empty }) => (
  <div className="flex flex-col h-full">
    <div className="flex items-center gap-2 mb-1">
      <div
        className="w-1 h-7 rounded-full"
        style={{ backgroundColor: accent }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold uppercase tracking-wide text-white/85 flex items-center gap-1.5">
          {icon}
          {title}
        </div>
        <div className="text-[10px] text-white/45 leading-tight">
          {subtitle}
        </div>
      </div>
    </div>
    <div className="flex-1 space-y-2 mt-2">
      {items.length === 0 ? (
        <div className="text-[11px] italic text-white/30 px-2 py-3">
          {empty}
        </div>
      ) : (
        items.map((it, idx) => (
          <div key={idx}>{renderRow(it)}</div>
        ))
      )}
    </div>
  </div>
);

const ExecutiveSummary = ({ summary, stationId, date, token }) => {
  const { t } = useLanguage();
  const [contentMix, setContentMix] = useState(null);

  // v2.13.19 — Fetch daily content-mix (music/speech/ads %) for the
  // ExecutiveSummary pie chart. Best-effort: if the endpoint fails or
  // returns no samples, we just don't render the pie.
  useEffect(() => {
    let cancelled = false;
    if (!stationId || !date || !token) {
      setContentMix(null);
      return;
    }
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/admin/content-mix/station/${stationId}?day=${date}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) return;
        const json = await r.json();
        if (!cancelled) setContentMix(json.weekly_summary || null);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [stationId, date, token]);

  if (!summary) return null;
  // v2.13.19 (2026-02): RESTORED worst_breaks / best_breaks sections.
  // Backend has always computed them — operator confirmed they're
  // useful now that we have classifier data to validate gap quality.
  // Keep the 4 sections in a single column on narrow screens and
  // 2-column grid on desktop so the operator can compare song impact
  // against break impact at a glance.
  const {
    hero_songs = [],
    villain_songs = [],
    best_breaks = [],
    worst_breaks = [],
    segment_dashboard = null,
  } = summary;

  const dashboardCount = segment_dashboard
    ? ['BREAK', 'CONTENT_BLOCK', 'PROGRAM_SEGMENT']
        .reduce((acc, k) => acc + (segment_dashboard[k]?.count || 0), 0)
    : 0;

  if (
    hero_songs.length === 0 && villain_songs.length === 0
    && best_breaks.length === 0 && worst_breaks.length === 0
    && dashboardCount === 0
  ) return null;

  // Best-breaks accent: green if we genuinely held/grew audience,
  // grey if it's just the "least bad" gap of the day.
  const bestAccent =
    best_breaks.length > 0 && best_breaks[0].delta > 0
      ? SEVERITY_HEX.strong_gain
      : best_breaks.length > 0 && best_breaks[0].delta === 0
      ? SEVERITY_HEX.stable
      : SEVERITY_HEX.minor_loss;

  return (
    <div
      className="bg-gradient-to-br from-slate-900/40 to-slate-900/20 border border-white/10 rounded-xl p-4 mt-6"
      data-testid="executive-summary"
    >
      <div className="mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-amber-400/90">
          {t('execSum_title')}
        </h3>
      </div>
      {/* v2.13.19 — Two-column layout: Content-Mix pie on the left,
          songs/breaks grid on the right. On mobile they stack. */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4">
        <div className="space-y-3">
          {contentMix && contentMix.samples_total > 0 && (
            <ContentMixPie
              data={contentMix}
              title={t('execSum_contentMixTitle') || 'Σύνθεση Ημέρας'}
            />
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hero_songs.length > 0 && (
            <Section
              title={t('execSum_heroSongs')}
              subtitle={t('execSum_heroSongs_subtitle')}
              accent={SEVERITY_HEX.strong_gain}
              icon={<Music2 className="w-3.5 h-3.5 text-emerald-400" />}
              items={hero_songs}
              empty={t('execSum_noData')}
              renderRow={(it) => <SongRow item={it} variant="hero" />}
            />
          )}
          {villain_songs.length > 0 && (
            <Section
              title={t('execSum_villainSongs')}
              subtitle={t('execSum_villainSongs_subtitle')}
              accent={SEVERITY_HEX.severe_loss}
              icon={<Music2 className="w-3.5 h-3.5 text-red-400" />}
              items={villain_songs}
              empty={t('execSum_noData')}
              renderRow={(it) => <SongRow item={it} variant="villain" />}
            />
          )}
          {best_breaks.length > 0 && (
            <Section
              title={t('execSum_bestBreaks') || 'Καλύτερα Breaks'}
              subtitle={t('execSum_bestBreaks_subtitle') || 'Κενά που κράτησαν ή μεγάλωσαν το κοινό'}
              accent={bestAccent}
              icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-400" />}
              items={best_breaks}
              empty={t('execSum_noData')}
              renderRow={(it) => (
                <BreakRow
                  item={it}
                  badgeHex={SEVERITY_HEX[it.severity_band] || SEVERITY_HEX.unknown}
                />
              )}
            />
          )}
          {worst_breaks.length > 0 && (
            <Section
              title={t('execSum_worstBreaks') || 'Χειρότερα Breaks'}
              subtitle={t('execSum_worstBreaks_subtitle') || 'Κενά με τις μεγαλύτερες αποχωρήσεις ακροατών'}
              accent={SEVERITY_HEX.severe_loss}
              icon={<TrendingDown className="w-3.5 h-3.5 text-red-400" />}
              items={worst_breaks}
              empty={t('execSum_noData')}
              renderRow={(it) => (
                <BreakRow
                  item={it}
                  variant="worst"
                  badgeHex={SEVERITY_HEX[it.severity_band] || SEVERITY_HEX.unknown}
                />
              )}
            />
          )}
        </div>
      </div>
      {/* v2.13.21 — Show Analysis: per-content_type dashboards.
          v2.13.111 — Fully hide the section (including the divider
          border) when there is no classifier evidence.
          v2.13.115 (2026-07-04) — DISABLED entirely per operator
          request. The 3-card dashboard was still surfacing "1 break,
          0 content blocks, 0 show segments" placeholder rows for
          days with mostly-music broadcasting and the operator found
          it noisy. Re-enable when we build proper show-segment
          classifiers (e.g. voice-over ratio, ad-jingle detection).
          To re-enable: change `false` to `segment_dashboard && dashboardCount > 0`
          in the condition below and remove this note. */}
      {false && segment_dashboard && dashboardCount > 0 && (
        <div className="mt-6 pt-6 border-t border-white/10">
          <ShowAnalysis dashboard={segment_dashboard} />
        </div>
      )}
    </div>
  );
};

export default ExecutiveSummary;
